/**
 * NavigatorX User Management
 *
 * Full user list with cursor-based pagination, global search,
 * status filtering, sorting, multi-select, and integration with
 * all existing user management modals and actions.
 *
 * Service functions wired:
 * - getUsers (paginated, filtered, sorted)
 * - subscribeToUsers (real-time for live count badge)
 * - All modals: UserDetailModal, EditUserModal, DeleteUserModal
 * - BulkActionsPanel for multi-user operations
 * - NotificationCenter for per-user comms
 * - UserActions for per-row actions
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  Users,
  UserCheck,
  UserX,
  Clock,
  Crown,
  Ban,
  Mail,
  Phone,
  Building2,
  Calendar,
  Eye,
  Edit,
  MoreVertical,
  X,
  Download,
  CheckSquare,
  Square,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { DocumentSnapshot } from 'firebase/firestore';
import { getUsers, timestampToDate } from '../../../utils/services/navigatorXService';
import { auth } from '../../../utils/firebase/client';
import type { NavUser, NavUserSubscriptionStatus, PaginatedResult } from '../../../types/navigatorx';

import UserDetailModal from './UserDetailModal';
import EditUserModal from './EditUserModal';
import DeleteUserModal from './DeleteUserModal';
import UserActions from './UserActions';
import BulkActionsPanel from './BulkActionsPanel';
import NotificationCenter from './NotificationCenter';
import CreateUserModal from './CreateUserModal';

// ============================================================================
// MODULE-LEVEL CONSTANTS AND PURE FUNCTIONS
// ============================================================================

const PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: NavUserSubscriptionStatus | 'ALL'; label: string; color: string }[] = [
  { value: 'ALL', label: 'All Statuses', color: 'bg-gray-100 text-gray-700' },
  { value: 'SUBSCRIBED', label: 'Subscribed', color: 'bg-green-100 text-green-800' },
  { value: 'TRIAL_ACTIVE', label: 'Trial Active', color: 'bg-blue-100 text-blue-800' },
  { value: 'TRIAL_EXPIRED', label: 'Trial Expired', color: 'bg-red-100 text-red-800' },
  { value: 'SUBSCRIPTION_EXPIRED', label: 'Sub Expired', color: 'bg-orange-100 text-orange-800' },
  { value: 'REFERRAL_FREE_MONTH', label: 'Free Month', color: 'bg-pink-100 text-pink-800' },
  { value: 'NONE', label: 'None', color: 'bg-gray-100 text-gray-600' },
  { value: 'EMAIL_VERIFIED', label: 'Email Verified', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'PHONE_VERIFIED', label: 'Phone Verified', color: 'bg-teal-100 text-teal-800' },
  { value: 'BLOCKED', label: 'Blocked', color: 'bg-red-100 text-red-800' },
];

type SortField = 'createdAt' | 'lastLoginAt' | 'email' | 'displayName' | 'subscriptionStatus';
type SortDirection = 'asc' | 'desc';

function getStatusBadge(status: NavUserSubscriptionStatus): { label: string; className: string } {
  switch (status) {
    case 'SUBSCRIBED':
    case 'active':
      return { label: 'Subscribed', className: 'bg-green-100 text-green-800' };
    case 'TRIAL_ACTIVE':
    case 'trial':
      return { label: 'Trial', className: 'bg-blue-100 text-blue-800' };
    case 'TRIAL_EXPIRED':
      return { label: 'Trial Expired', className: 'bg-red-100 text-red-800' };
    case 'SUBSCRIPTION_EXPIRED':
    case 'expired':
      return { label: 'Expired', className: 'bg-orange-100 text-orange-800' };
    case 'REFERRAL_FREE_MONTH':
      return { label: 'Free Month', className: 'bg-pink-100 text-pink-800' };
    case 'BLOCKED':
    case 'cancelled':
      return { label: 'Blocked', className: 'bg-red-100 text-red-800 border border-red-300' };
    case 'NONE':
    case 'none':
      return { label: 'None', className: 'bg-gray-100 text-gray-600' };
    case 'EMAIL_VERIFIED':
      return { label: 'Email Verified', className: 'bg-cyan-100 text-cyan-800' };
    case 'PHONE_VERIFIED':
      return { label: 'Phone Verified', className: 'bg-teal-100 text-teal-800' };
    case 'grace':
      return { label: 'Grace', className: 'bg-yellow-100 text-yellow-800' };
    case 'invited':
      return { label: 'Invited', className: 'bg-purple-100 text-purple-800' };
    default:
      return { label: String(status), className: 'bg-gray-100 text-gray-600' };
  }
}

function formatTimestamp(ts: unknown): string {
  if (!ts) return '—';
  const date = timestampToDate(ts);
  if (!date) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimestampRelative(ts: unknown): string {
  if (!ts) return 'Never';
  const date = timestampToDate(ts);
  if (!date) return 'Never';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getUserDisplayName(user: NavUser): string {
  if (user.displayName) return user.displayName;
  if (user.firstName || user.lastName) {
    return [user.firstName, user.lastName].filter(Boolean).join(' ');
  }
  return user.email || user.phone || user.id.slice(0, 8);
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

// ============================================================================
// COMPONENT TYPES
// ============================================================================

interface UserManagementProps {
  embedded?: boolean;
}

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'detail'; user: NavUser }
  | { type: 'edit'; user: NavUser }
  | { type: 'delete'; user: NavUser }
  | { type: 'notify'; user: NavUser };

interface PageCursor {
  lastDoc: DocumentSnapshot | null;
  pageNumber: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function UserManagement({ embedded }: UserManagementProps) {
  // User data
  const [users, setUsers] = useState<NavUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const cursorStackRef = useRef<PageCursor[]>([{ lastDoc: null, pageNumber: 1 }]);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<NavUserSubscriptionStatus | 'ALL'>('ALL');
  const [showStatusFilter, setShowStatusFilter] = useState(false);

  // Sorting
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Selection
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // Modals
  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  // Stats
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  // Debounce timer for search
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadUsers = useCallback(async (
    page: number,
    cursor: DocumentSnapshot | null,
    isRefresh: boolean = false
  ) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const result: PaginatedResult<NavUser> = await getUsers({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        searchTerm: searchTerm || undefined,
        pageSize: PAGE_SIZE,
        lastDoc: cursor || undefined,
        orderByField: sortField,
        orderDirection: sortDirection,
      });

      setUsers(result.items);
      setHasMore(result.hasMore);
      setTotalLoaded(result.items.length);
      setCurrentPage(page);

      // Store cursor for this page for forward navigation
      if (result.lastDoc) {
        const existingIdx = cursorStackRef.current.findIndex(c => c.pageNumber === page + 1);
        const newCursor: PageCursor = { lastDoc: result.lastDoc, pageNumber: page + 1 };
        if (existingIdx >= 0) {
          cursorStackRef.current[existingIdx] = newCursor;
        } else {
          cursorStackRef.current.push(newCursor);
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, searchTerm, sortField, sortDirection]);

  // Load status counts on mount for the filter badges
  const loadStatusCounts = useCallback(async () => {
    try {
      // Load a large batch to count statuses (up to 500 users)
      const result = await getUsers({ pageSize: 500 });
      const counts: Record<string, number> = {};
      let total = 0;
      for (const user of result.items) {
        const st = user.subscriptionStatus || 'NONE';
        counts[st] = (counts[st] || 0) + 1;
        total++;
      }
      counts['ALL'] = total;
      setStatusCounts(counts);
    } catch {
      // Non-critical, silently fail
    }
  }, []);

  // Initial load
  useEffect(() => {
    cursorStackRef.current = [{ lastDoc: null, pageNumber: 1 }];
    loadUsers(1, null);
    loadStatusCounts();
  }, [loadUsers, loadStatusCounts]);

  // Reset pagination when filters change
  useEffect(() => {
    cursorStackRef.current = [{ lastDoc: null, pageNumber: 1 }];
    loadUsers(1, null);
  }, [statusFilter, searchTerm, sortField, sortDirection, loadUsers]);

  // Debounced search
  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchTerm(value.trim());
    }, 400);
  };

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // ============================================================================
  // PAGINATION HANDLERS
  // ============================================================================

  const handleNextPage = () => {
    if (!hasMore) return;
    const nextCursor = cursorStackRef.current.find(c => c.pageNumber === currentPage + 1);
    if (nextCursor) {
      loadUsers(currentPage + 1, nextCursor.lastDoc);
    }
  };

  const handlePrevPage = () => {
    if (currentPage <= 1) return;
    if (currentPage === 2) {
      // Going back to page 1 means no cursor
      loadUsers(1, null);
    } else {
      const prevCursor = cursorStackRef.current.find(c => c.pageNumber === currentPage - 1);
      if (prevCursor) {
        loadUsers(currentPage - 1, prevCursor.lastDoc);
      } else {
        // Fallback: reload from beginning
        cursorStackRef.current = [{ lastDoc: null, pageNumber: 1 }];
        loadUsers(1, null);
      }
    }
  };

  const handleFirstPage = () => {
    if (currentPage === 1) return;
    cursorStackRef.current = [{ lastDoc: null, pageNumber: 1 }];
    loadUsers(1, null);
  };

  // ============================================================================
  // SORTING
  // ============================================================================

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-gray-400" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 text-blue-600" />
      : <ArrowDown className="h-3 w-3 text-blue-600" />;
  };

  // ============================================================================
  // SELECTION
  // ============================================================================

  const toggleSelectUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === users.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(users.map(u => u.id)));
    }
  };

  const selectedUsers = users.filter(u => selectedUserIds.has(u.id));

  const handleClearSelection = () => setSelectedUserIds(new Set());

  const handleRemoveFromSelection = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
  };

  // ============================================================================
  // REFRESH & MODAL HANDLERS
  // ============================================================================

  const handleRefresh = () => {
    cursorStackRef.current = [{ lastDoc: null, pageNumber: 1 }];
    loadUsers(1, null, true);
    loadStatusCounts();
    setSelectedUserIds(new Set());
  };

  const handleUserUpdated = (updatedUser: NavUser) => {
    setUsers(prev => prev.map(u => u.id === updatedUser.id ? updatedUser : u));
    setModal({ type: 'none' });
    toast.success('User updated');
  };

  const handleUserDeleted = (userId: string) => {
    setUsers(prev => prev.filter(u => u.id !== userId));
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
    setModal({ type: 'none' });
    toast.success('User deleted');
  };

  const handleUserCreated = (newUser: NavUser) => {
    setUsers(prev => [newUser, ...prev]);
    setModal({ type: 'none' });
    loadStatusCounts();
  };

  // ============================================================================
  // CSV EXPORT (ALL FILTERED RESULTS)
  // ============================================================================

  const handleExportAll = async () => {
    try {
      toast.info('Exporting users...');
      const result = await getUsers({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        searchTerm: searchTerm || undefined,
        pageSize: 5000,
        orderByField: sortField,
        orderDirection: sortDirection,
      });

      const headers = [
        'ID', 'Email', 'Phone', 'First Name', 'Last Name', 'Display Name',
        'Company', 'Status', 'Platform', 'App Version', 'Referral Code',
        'Login Count', 'Last Login', 'Created At'
      ];

      const rows = result.items.map(user => [
        user.id,
        user.email || '',
        user.phone || '',
        user.firstName || '',
        user.lastName || '',
        user.displayName || '',
        user.company || '',
        user.subscriptionStatus,
        user.platform || '',
        user.appVersion || '',
        user.referralCode || '',
        user.loginCount || 0,
        formatTimestamp(user.lastLoginAt),
        formatTimestamp(user.createdAt),
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `navigatorx_users_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      toast.success(`Exported ${result.items.length} users to CSV`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  const allSelected = users.length > 0 && selectedUserIds.size === users.length;
  const someSelected = selectedUserIds.size > 0 && selectedUserIds.size < users.length;

  return (
    <div className="space-y-4">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: statusCounts['ALL'] || 0, icon: Users, color: 'text-gray-600', filterValue: 'ALL' as const },
          { label: 'Subscribed', value: statusCounts['SUBSCRIBED'] || 0, icon: Crown, color: 'text-green-600', filterValue: 'SUBSCRIBED' as const },
          { label: 'Trial', value: statusCounts['TRIAL_ACTIVE'] || 0, icon: Clock, color: 'text-blue-600', filterValue: 'TRIAL_ACTIVE' as const },
          { label: 'Expired', value: (statusCounts['TRIAL_EXPIRED'] || 0) + (statusCounts['SUBSCRIPTION_EXPIRED'] || 0), icon: UserX, color: 'text-orange-600', filterValue: 'SUBSCRIPTION_EXPIRED' as const },
          { label: 'Blocked', value: statusCounts['BLOCKED'] || 0, icon: Ban, color: 'text-red-600', filterValue: 'BLOCKED' as const },
        ].map(stat => (
          <Card
            key={stat.label}
            className={`cursor-pointer transition-colors hover:border-blue-300 ${
              statusFilter === stat.filterValue ? 'border-blue-500 bg-blue-50' : ''
            }`}
            onClick={() => setStatusFilter(stat.filterValue)}
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                  <p className="text-xl font-bold">{stat.value.toLocaleString()}</p>
                </div>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Filter Toolbar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, phone, company, referral code..."
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                className="pl-10 pr-8"
              />
              {searchInput && (
                <button
                  onClick={() => { setSearchInput(''); setSearchTerm(''); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Status Filter Dropdown */}
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowStatusFilter(prev => !prev)}
                className="w-full md:w-auto gap-2"
              >
                <Filter className="h-4 w-4" />
                {STATUS_OPTIONS.find(s => s.value === statusFilter)?.label || 'Filter'}
                {statusFilter !== 'ALL' && (
                  <Badge className="ml-1 bg-blue-100 text-blue-700 text-xs px-1.5">1</Badge>
                )}
              </Button>
              {showStatusFilter && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowStatusFilter(false)} />
                  <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border z-20 py-1">
                    {STATUS_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => { setStatusFilter(opt.value); setShowStatusFilter(false); }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                          statusFilter === opt.value ? 'bg-blue-50 text-blue-700' : ''
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full ${opt.color.split(' ')[0]}`} />
                          {opt.label}
                        </span>
                        {statusCounts[opt.value] !== undefined && (
                          <span className="text-xs text-gray-400">{statusCounts[opt.value]}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                variant="amber"
                onClick={() => setModal({ type: 'create' })}
                className="gap-2"
              >
                <UserPlus className="h-4 w-4" />
                <span className="hidden md:inline">Create User</span>
              </Button>
              <Button variant="outline" size="icon" onClick={handleRefresh} disabled={refreshing}>
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline" onClick={handleExportAll} className="gap-2">
                <Download className="h-4 w-4" />
                <span className="hidden md:inline">Export</span>
              </Button>
            </div>
          </div>

          {/* Active filters display */}
          {(statusFilter !== 'ALL' || searchTerm) && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t">
              <span className="text-xs text-gray-500">Active filters:</span>
              {statusFilter !== 'ALL' && (
                <Badge
                  className="gap-1 cursor-pointer hover:bg-gray-200"
                  variant="secondary"
                  onClick={() => setStatusFilter('ALL')}
                >
                  Status: {STATUS_OPTIONS.find(s => s.value === statusFilter)?.label}
                  <X className="h-3 w-3" />
                </Badge>
              )}
              {searchTerm && (
                <Badge
                  className="gap-1 cursor-pointer hover:bg-gray-200"
                  variant="secondary"
                  onClick={() => { setSearchInput(''); setSearchTerm(''); }}
                >
                  Search: &quot;{searchTerm}&quot;
                  <X className="h-3 w-3" />
                </Badge>
              )}
              <button
                onClick={() => { setStatusFilter('ALL'); setSearchInput(''); setSearchTerm(''); }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Clear all
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-3 text-gray-500">Loading users...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No users found</p>
              <p className="text-sm mt-1">
                {searchTerm || statusFilter !== 'ALL'
                  ? 'Try adjusting your search or filters'
                  : 'No NavigatorX users registered yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    {/* Select All */}
                    <th className="p-3 w-10">
                      <button onClick={toggleSelectAll} className="flex items-center justify-center">
                        {allSelected ? (
                          <CheckSquare className="h-4 w-4 text-blue-600" />
                        ) : someSelected ? (
                          <div className="h-4 w-4 border-2 border-blue-600 rounded-sm bg-blue-100 flex items-center justify-center">
                            <div className="h-1.5 w-1.5 bg-blue-600 rounded-sm" />
                          </div>
                        ) : (
                          <Square className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </th>
                    {/* User */}
                    <th className="p-3 text-left">
                      <button
                        onClick={() => handleSort('displayName')}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wide hover:text-gray-900"
                      >
                        User
                        <SortIcon field="displayName" />
                      </button>
                    </th>
                    {/* Status */}
                    <th className="p-3 text-left">
                      <button
                        onClick={() => handleSort('subscriptionStatus')}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wide hover:text-gray-900"
                      >
                        Status
                        <SortIcon field="subscriptionStatus" />
                      </button>
                    </th>
                    {/* Contact */}
                    <th className="p-3 text-left hidden lg:table-cell">
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Contact</span>
                    </th>
                    {/* Platform */}
                    <th className="p-3 text-left hidden xl:table-cell">
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Platform</span>
                    </th>
                    {/* Last Login */}
                    <th className="p-3 text-left hidden md:table-cell">
                      <button
                        onClick={() => handleSort('lastLoginAt')}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wide hover:text-gray-900"
                      >
                        Last Login
                        <SortIcon field="lastLoginAt" />
                      </button>
                    </th>
                    {/* Created */}
                    <th className="p-3 text-left hidden lg:table-cell">
                      <button
                        onClick={() => handleSort('createdAt')}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wide hover:text-gray-900"
                      >
                        Created
                        <SortIcon field="createdAt" />
                      </button>
                    </th>
                    {/* Actions */}
                    <th className="p-3 w-10">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => {
                    const isSelected = selectedUserIds.has(user.id);
                    const statusBadge = getStatusBadge(user.subscriptionStatus);

                    return (
                      <tr
                        key={user.id}
                        className={`border-b last:border-0 hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="p-3">
                          <button onClick={() => toggleSelectUser(user.id)}>
                            {isSelected ? (
                              <CheckSquare className="h-4 w-4 text-blue-600" />
                            ) : (
                              <Square className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                            )}
                          </button>
                        </td>

                        {/* User Name + Avatar */}
                        <td className="p-3">
                          <button
                            onClick={() => setModal({ type: 'detail', user })}
                            className="flex items-center gap-3 text-left hover:text-blue-600 transition-colors"
                          >
                            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                              {(user.firstName?.[0] || user.email?.[0] || '?').toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate max-w-[200px]">
                                {getUserDisplayName(user)}
                              </p>
                              {user.company && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <Building2 className="h-3 w-3" />
                                  {user.company}
                                </p>
                              )}
                            </div>
                          </button>
                        </td>

                        {/* Status */}
                        <td className="p-3">
                          <Badge className={`text-xs ${statusBadge.className}`}>
                            {statusBadge.label}
                          </Badge>
                        </td>

                        {/* Contact */}
                        <td className="p-3 hidden lg:table-cell">
                          <div className="space-y-1">
                            {user.email && (
                              <p className="text-xs text-gray-600 flex items-center gap-1 truncate max-w-[200px]">
                                <Mail className="h-3 w-3 flex-shrink-0" />
                                {user.email}
                              </p>
                            )}
                            {user.phone && (
                              <p className="text-xs text-gray-600 flex items-center gap-1">
                                <Phone className="h-3 w-3 flex-shrink-0" />
                                {user.phone}
                              </p>
                            )}
                          </div>
                        </td>

                        {/* Platform */}
                        <td className="p-3 hidden xl:table-cell">
                          <div className="text-xs text-gray-600">
                            {user.platform && (
                              <Badge variant="outline" className="text-xs capitalize">
                                {user.platform}
                              </Badge>
                            )}
                            {user.appVersion && (
                              <p className="text-gray-400 mt-0.5">v{user.appVersion}</p>
                            )}
                          </div>
                        </td>

                        {/* Last Login */}
                        <td className="p-3 hidden md:table-cell">
                          <span className="text-xs text-gray-600">
                            {formatTimestampRelative(user.lastLoginAt)}
                          </span>
                        </td>

                        {/* Created */}
                        <td className="p-3 hidden lg:table-cell">
                          <span className="text-xs text-gray-600">
                            {formatTimestamp(user.createdAt)}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="p-3">
                          <UserActions
                            user={user}
                            onEdit={() => setModal({ type: 'edit', user })}
                            onDelete={() => setModal({ type: 'delete', user })}
                            onRefresh={handleRefresh}
                            variant="dropdown"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Footer */}
          {!loading && users.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{((currentPage - 1) * PAGE_SIZE) + totalLoaded} users
                {selectedUserIds.size > 0 && (
                  <span className="ml-2 text-blue-600 font-medium">
                    ({selectedUserIds.size} selected)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFirstPage}
                  disabled={currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Prev
                </Button>
                <span className="text-sm font-medium px-3">
                  Page {currentPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!hasMore}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Actions (floating bar when users selected) */}
      <BulkActionsPanel
        selectedUsers={selectedUsers}
        onClearSelection={handleClearSelection}
        onRefresh={handleRefresh}
        onRemoveFromSelection={handleRemoveFromSelection}
      />

      {/* Modals */}
      {modal.type === 'detail' && (
        <UserDetailModal
          user={modal.user}
          onClose={() => setModal({ type: 'none' })}
          onEdit={() => setModal({ type: 'edit', user: modal.user })}
          onDelete={() => setModal({ type: 'delete', user: modal.user })}
        />
      )}

      {modal.type === 'edit' && (
        <EditUserModal
          user={modal.user}
          onClose={() => setModal({ type: 'none' })}
          onSave={handleUserUpdated}
        />
      )}

      {modal.type === 'delete' && (
        <DeleteUserModal
          user={modal.user}
          onClose={() => setModal({ type: 'none' })}
          onDelete={handleUserDeleted}
        />
      )}

      {modal.type === 'notify' && (
        <NotificationCenter
          user={modal.user}
          onClose={() => setModal({ type: 'none' })}
          onSent={handleRefresh}
        />
      )}

      {modal.type === 'create' && (
        <CreateUserModal
          onClose={() => setModal({ type: 'none' })}
          onCreated={handleUserCreated}
        />
      )}
    </div>
  );
}
